/**
 * The two editing boxes any screen can open:
 *
 * - **Set a value** ("Set Album for 12 songs"): what the songs hold now, with
 *   counts, one click to take one of those values, or type a new one. Says how
 *   many songs will change before anything is written.
 * - **Review values** (a column's ⋯): every spelling of that column across the
 *   library, with counts. Tick several and merge them into one, or open the songs
 *   behind any value -- the empty ones included -- in the tag editor.
 *
 * Both write through core/edits, so every change has Undo and lands in history.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useApp } from "../core/app";
import {
  announce,
  editableColumns,
  fetchValues,
  parseValue,
  showValue,
  writeValues,
  type EditableColumn,
  type ValueCount,
} from "../core/edits";
import { songs } from "../format/count";
import { REASONS, flagSongs, reasonNotes } from "../core/needsEdit";
import { setSongsAvailability } from "../api";
import { toast } from "./Toast";
import { Icon } from "./Icons";

type Open =
  | { kind: "set"; trackIds: string[]; col: EditableColumn }
  | { kind: "review"; col: EditableColumn }
  | { kind: "flag"; trackIds: string[] }
  | { kind: "release"; trackIds: string[]; current: string | null }
  | null;

let current: Open = null;
const listeners = new Set<() => void>();
const set = (next: Open) => {
  current = next;
  listeners.forEach((fn) => fn());
};

/** "Set <column> for these songs…" */
export const openSetValue = (trackIds: string[], col: EditableColumn) => set({ kind: "set", trackIds, col });
/** "Review values of <column>…" */
export const openReview = (col: EditableColumn) => set({ kind: "review", col });
/** "Flag for editing…": why these songs need an edit (core/needsEdit). */
export const openFlag = (trackIds: string[]) => set({ kind: "flag", trackIds });
/** "Release date…": nothing plays these songs by itself before then (nova.library.availability). */
export const openRelease = (trackIds: string[], current: string | null) => set({ kind: "release", trackIds, current });

export function EditDialogs() {
  const open = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current
  );
  if (!open) return null;
  if (open.kind === "flag") return <Flag trackIds={open.trackIds} />;
  if (open.kind === "release") return <Release trackIds={open.trackIds} current={open.current} />;
  return open.kind === "set" ? <SetValue trackIds={open.trackIds} col={open.col} /> : <Review col={open.col} />;
}

export function Modal({ title, children, onClose, wide = false }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <div className="nv-modal" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`nv-dialog nv-box ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="nv-iconbtn" title="Close (Esc)" onClick={onClose}><Icon name="close" /></button>
        </header>
        {children}
      </div>
    </div>
  );
}

function label(value: unknown) {
  return value === null || value === undefined ? <em className="nv-dim">empty</em> : showValue(value);
}

function SetValue({ trackIds, col }: { trackIds: string[]; col: EditableColumn }) {
  const app = useApp();
  const [values, setValues] = useState<ValueCount[] | null>(null);
  const [library, setLibrary] = useState<ValueCount[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => set(null);

  useEffect(() => {
    void fetchValues(col.id, trackIds).then((r) => {
      setValues(r.values);
      // One value among them: start from it, so a small fix is a small edit.
      if (r.values.length === 1) setText(showValue(r.values[0].value));
    });
    void fetchValues(col.id).then((r) => setLibrary(r.values.filter((v) => v.value !== null).slice(0, 200)));
  }, [col.id, trackIds]);

  const parsed = parseValue(col, text);
  const changing = useMemo(() => {
    if (!values) return 0;
    return values.filter((v) => showValue(v.value) !== showValue(parsed.value)).reduce((n, v) => n + v.count, 0);
  }, [values, parsed.value]);

  const save = async () => {
    if (parsed.error) return setError(parsed.error);
    setBusy(true);
    const outcome = await writeValues(trackIds, [{ col, value: parsed.value }]);
    setBusy(false);
    announce(outcome, `${col.label} set for ${songs(trackIds.length)}.`, () => void app.library.refresh());
    if (!outcome.error || outcome.failed.length) close();
    else setError(outcome.error);
  };

  const many = trackIds.length > 1;
  return (
    <Modal title={many ? `Set ${col.label} for ${songs(trackIds.length)}` : `Edit ${col.label}`} onClose={close}>
      <div className="nv-box-body">
        {many && (
          <>
            <p className="nv-dim">What they hold now. Click one to use it for all of them.</p>
            <ul className="nv-valuelist">
              {(values ?? []).map((v) => (
                <li key={showValue(v.value) || "∅"}>
                  <button onClick={() => setText(showValue(v.value))} disabled={v.value === null}>
                    <span className="nv-valuelist-v">{label(v.value)}</span>
                    <span className="nv-count">{v.count}</span>
                  </button>
                </li>
              ))}
              {values === null && <li className="nv-dim">Reading…</li>}
            </ul>
          </>
        )}
        <label className="nv-field">
          <span>{many ? `New ${col.label.toLowerCase()}` : col.label}</span>
          {col.long ? (
            <textarea className="nv-input" rows={4} value={text} autoFocus onChange={(e) => { setText(e.target.value); setError(null); }} />
          ) : (
            <input
              className="nv-input"
              value={text}
              autoFocus
              list={`nv-values-${col.id}`}
              placeholder={col.type === "date" ? "2024-05-31" : col.type === "boolean" ? "yes or no" : "Empty clears it"}
              onChange={(e) => { setText(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) void save(); }}
            />
          )}
          <datalist id={`nv-values-${col.id}`}>
            {library.map((v) => <option key={showValue(v.value)} value={showValue(v.value)} />)}
          </datalist>
        </label>
        {(error || parsed.error) && <div className="nv-warn bad">{error ?? parsed.error}</div>}
      </div>
      <footer className="nv-box-foot">
        <span className="nv-dim">
          {values === null ? "" : changing ? `${songs(changing)} will change.` : "Nothing will change."}
          {col.kind === "tag" ? " Written into the files; the audio is not touched." : " Stored in Nova."}
        </span>
        <span className="nv-spacer" />
        <button className="nv-btn" onClick={close}>Cancel</button>
        <button className="nv-btn primary" disabled={busy || !!parsed.error || !changing} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </footer>
    </Modal>
  );
}

function Review({ col }: { col: EditableColumn }) {
  const app = useApp();
  const [values, setValues] = useState<ValueCount[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [into, setInto] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => set(null);
  const keyOf = (v: ValueCount) => (v.value === null ? "\u0000empty" : showValue(v.value));

  const load = () => void fetchValues(col.id).then((r) => setValues(r.values));
  useEffect(load, [col.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = (values ?? []).filter((v) => !filter || showValue(v.value).toLowerCase().includes(filter.toLowerCase()));
  const picked = (values ?? []).filter((v) => chosen.has(keyOf(v)));
  const pickedIds = picked.flatMap((v) => v.trackIds);
  const toggle = (v: ValueCount) =>
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(keyOf(v))) next.delete(keyOf(v));
      else next.add(keyOf(v));
      return next;
    });

  const edit = (ids: string[]) => {
    close();
    app.navigate("studio", { ids: ids.join(",") });
  };

  const merge = async () => {
    const parsed = parseValue(col, into);
    if (parsed.error || !pickedIds.length) return;
    setBusy(true);
    const outcome = await writeValues(pickedIds, [{ col, value: parsed.value }]);
    setBusy(false);
    announce(outcome, `${songs(pickedIds.length)} now have ${col.label.toLowerCase()} “${into.trim() || "empty"}”.`, () => {
      void app.library.refresh();
      load();
    });
    setChosen(new Set());
  };

  return (
    <Modal title={`Review values: ${col.label}`} onClose={close} wide>
      <div className="nv-box-body">
        <div className="nv-box-tools">
          <input className="nv-input" type="search" placeholder={`Filter ${col.label.toLowerCase()} values`} value={filter} onChange={(e) => setFilter(e.target.value)} />
          <span className="nv-dim">{values ? `${values.length.toLocaleString()} different values` : "Reading…"}</span>
        </div>
        <ul className="nv-valuelist review">
          {shown.map((v) => (
            <li key={keyOf(v)} className={chosen.has(keyOf(v)) ? "on" : ""}>
              <label className="nv-valuelist-pick">
                <input type="checkbox" checked={chosen.has(keyOf(v))} onChange={() => toggle(v)} />
                <span className="nv-valuelist-v">{label(v.value)}</span>
              </label>
              <span className="nv-count">{v.count}</span>
              <button className="nv-link" onClick={() => edit(v.trackIds)} title="Open these songs in the tag editor">
                Edit {v.count === 1 ? "it" : `these ${v.count}`}
              </button>
              {v.value !== null && (
                <button className="nv-link" onClick={() => { setInto(showValue(v.value)); }} title="Use this spelling for the merge">
                  Use this spelling
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      <footer className="nv-box-foot">
        {picked.length ? (
          <>
            <span>Merge {picked.length} value{picked.length === 1 ? "" : "s"} ({songs(pickedIds.length)}) into</span>
            <input className="nv-input" value={into} placeholder={`${col.label}`} onChange={(e) => setInto(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void merge(); }} />
            <button className="nv-btn" onClick={() => edit(pickedIds)}>Edit in tag editor</button>
            <button className="nv-btn primary" disabled={busy || !into.trim()} onClick={() => void merge()}>
              {busy ? "Saving…" : "Merge"}
            </button>
          </>
        ) : (
          <span className="nv-dim">Tick values to merge them into one spelling, or open the songs behind any value.</span>
        )}
        <span className="nv-spacer" />
        <button className="nv-btn" onClick={close}>Done</button>
      </footer>
    </Modal>
  );
}

/** For menus: every column a person can review. */
export function reviewableColumns(custom: Parameters<typeof editableColumns>[0]) {
  return editableColumns(custom);
}

/** Why these songs need an edit: one of the usual reasons or a word of your own, and a note. */
function Flag({ trackIds }: { trackIds: string[] }) {
  const [reason, setReason] = useState("tags");
  const [own, setOwn] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => set(null);
  const chosen = reason === "other" ? own.trim() : reason;
  const save = async () => {
    if (!chosen) return;
    setBusy(true);
    const ok = await flagSongs(trackIds, chosen, note);
    setBusy(false);
    if (ok) close();
  };
  return (
    <Modal title={trackIds.length > 1 ? `Flag ${songs(trackIds.length)} for editing` : "Flag for editing"} onClose={close}>
      <div className="nv-box-body">
        <p className="nv-dim">Whoever edits songs sees it in the inbox and under Needs edit, until someone marks it fixed.</p>
        <div className="nv-reasons" role="radiogroup" aria-label="Why">
          {[...REASONS, { id: "other", label: "Something else", hint: "Say what in a word or two." }].map((r) => (
            <label key={r.id} className={`nv-reason ${reason === r.id ? "on" : ""}`} title={r.hint}>
              <input type="radio" name="nv-reason" checked={reason === r.id} onChange={() => setReason(r.id)} />
              <b>{r.label}</b>
              <span>{r.hint}</span>
            </label>
          ))}
        </div>
        {reason === "other" && (
          <label className="nv-field">
            <span>What needs doing</span>
            <input className="nv-input" value={own} maxLength={40} autoFocus onChange={(e) => setOwn(e.target.value)} />
          </label>
        )}
        <label className="nv-field">
          <span>Note (optional)</span>
          <textarea className="nv-input" rows={2} maxLength={500} value={note} placeholder="Where, what to change…" onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      <footer className="nv-box-foot">
        <span className="nv-dim">{reasonNotes(chosen).join(" ") || "Nothing in the file changes."}</span>
        <span className="nv-spacer" />
        <button className="nv-btn" onClick={close}>Cancel</button>
        <button className="nv-btn primary" disabled={busy || !chosen} onClick={() => void save()}>{busy ? "Flagging…" : "Flag"}</button>
      </footer>
    </Modal>
  );
}

/**
 * A release date and time: before it, Auto DJ, Flow and the clip rules do not
 * play the song and a scheduled playlist skips it. Adding it by hand still
 * works, with a warning.
 */
function Release({ trackIds, current }: { trackIds: string[]; current: string | null }) {
  const [when, setWhen] = useState(current ?? "");
  const [busy, setBusy] = useState(false);
  const close = () => set(null);
  const save = async (value: string | null) => {
    setBusy(true);
    const out = await setSongsAvailability(trackIds, { availableFrom: value });
    setBusy(false);
    if (out.error) return toast(out.error, "bad");
    toast(value
      ? `${trackIds.length > 1 ? songs(trackIds.length) : "The song"} will be picked from ${value.replace("T", " ")}.`
      : `${trackIds.length > 1 ? songs(trackIds.length) : "The song"} can be picked now.`, "ok");
    close();
  };
  return (
    <Modal title={trackIds.length > 1 ? `Release date for ${songs(trackIds.length)}` : "Release date"} onClose={close}>
      <div className="nv-box-body">
        <p className="nv-dim">
          Before this date and time, Auto DJ, Flow and the clip rules leave the song out, and a scheduled playlist
          skips it. You can still add it yourself; you are warned.
        </p>
        <label className="nv-field">
          <span>Available from</span>
          <input
            className="nv-input"
            type="datetime-local"
            aria-label="Available from"
            value={when}
            autoFocus
            onChange={(e) => setWhen(e.target.value.slice(0, 16))}
          />
        </label>
      </div>
      <footer className="nv-box-foot">
        {current && <button className="nv-btn" disabled={busy} onClick={() => void save(null)}>Clear the date</button>}
        <span className="nv-spacer" />
        <button className="nv-btn" onClick={close}>Cancel</button>
        <button className="nv-btn primary" disabled={busy || !when} onClick={() => void save(when)}>{busy ? "Saving…" : "Save"}</button>
      </footer>
    </Modal>
  );
}
